import { useState, useEffect } from 'react';
import './App.css';
import { content } from './content';
import { SERVICE_PANEL_BACKGROUNDS } from './servicePanelBackgrounds';

function App() {
  const [lang, setLang] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = window.localStorage.getItem('basira-lang');
      if (saved === 'fr' || saved === 'en') return saved;
    }
    return 'fr';
  });

  useEffect(() => {
    document.documentElement.lang = lang;
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('basira-lang', lang);
    }
  }, [lang]);

  const t = content[lang];
  const isEn = lang === 'en';
  const enquiryMailto = `mailto:sarah@basira.me?subject=${encodeURIComponent(t.contact.enquirySubject)}&body=${encodeURIComponent(t.contact.enquiryBody)}`;

  return (
    <div className="app">
      <header className="site-header">
        <a href="/" className="logo-link" aria-label="Basira home">
          <span className="logo-crop logo-crop-header">
            <img src="/basira-logo.png" alt="Basira — Data & AI Consultancy" className="site-logo" />
          </span>
        </a>
        <nav className="nav">
          <a href="#services">{t.nav.services}</a>
          <a href="#why-basira">{t.nav.why}</a>
          <a href="#mission">{t.nav.mission}</a>
          <a href="#team">{t.nav.team}</a>
          <a href="#contact">{t.nav.contact}</a>
          <button
            type="button"
            className="lang-toggle"
            onClick={() => setLang(isEn ? 'fr' : 'en')}
            aria-label={isEn ? 'Passer en français' : 'Switch to English'}
          >
            {isEn ? 'FR' : 'EN'}
          </button>
        </nav>
      </header>

      <main>
        <section className="hero">
          <div className="hero-bg" aria-hidden="true" />
          <div className="hero-content">
            <span className="logo-crop logo-crop-hero">
              <img src="/basira-logo-white.png" alt="" className="hero-logo" />
            </span>
            <p className="hero-tagline">{t.hero.tagline}</p>
            <p className="hero-desc">{t.hero.desc}</p>
            <div className="hero-cta">
              <a href="#contact" className="btn btn-primary">{t.hero.ctaPrimary}</a>
              <a href="#services" className="btn btn-ghost">{t.hero.ctaSecondary}</a>
            </div>
          </div>
        </section>

        <section className="clients-banner" aria-label="Companies we have worked with">
          <p className="clients-banner-label">{t.clientsBanner}</p>
          <div className="clients-marquee-wrap">
            <div className="clients-marquee" aria-hidden="true">
              {[...t.clients, ...t.clients, ...t.clients].map((client, i) => (
                <div key={i} className="clients-marquee-item">
                  <img src={client.logo} alt={client.name} className={`clients-logo ${client.logoClass || ''}`.trim()} />
                  <span className="clients-name-fallback">{client.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="services" className="section section-services">
          <div className="container">
            <h2 className="section-title">{t.services.title}</h2>
            <p className="section-lead">{t.services.lead}</p>
          </div>
          <div className="services-pillars-wrap">
            <ul className="services-pillars">
              {t.services.items.map((service, i) => (
                <li
                  key={i}
                  className="service-pillar"
                  style={{
                    backgroundImage: `url(${SERVICE_PANEL_BACKGROUNDS[i]})`,
                  }}
                >
                  <div className="service-pillar-overlay" aria-hidden="true" />
                  <div className="service-pillar-inner">
                    <div className="service-pillar-top">
                      <img
                        src="/basira-logo-white.png"
                        alt=""
                        className="service-pillar-logo"
                      />
                    </div>
                    <div className="service-pillar-middle">
                      <h3 className="service-pillar-headline">{service.title}</h3>
                      <p className="service-pillar-body">{service.description}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="why-basira" className="section section-why">
          <div className="container">
            <h2 className="section-title">{t.why.title}</h2>
            <p className="section-lead" dangerouslySetInnerHTML={{ __html: t.why.lead }} />
            <ul className="why-list">
              {t.why.list.map((item, i) => (
                <li key={i}>
                  <strong>{item.strong}</strong> {item.text}
                </li>
              ))}
            </ul>

            <div className="section-why-mission">
              <h2 id="mission" className="section-title section-why-mission-title">
                {t.mission.title}
              </h2>
              <div className="mission-top">
                <div className="mission-block">
                  <h3 className="mission-block-title">{t.mission.visionTitle}</h3>
                  <p className="mission-block-text">{t.mission.visionText}</p>
                </div>
                <div className="mission-block">
                  <h3 className="mission-block-title">{t.mission.missionTitle}</h3>
                  <p className="mission-block-text">{t.mission.missionText}</p>
                </div>
              </div>
              <h3 className="mission-values-heading">{t.mission.valuesTitle}</h3>
              <div className="mission-values-grid">
                {t.mission.values.map((value, i) => (
                  <div key={i} className="mission-block">
                    <h4 className="mission-block-title">{value.title}</h4>
                    <p className="mission-block-text">{value.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="team" className="section section-team">
          <div className="container">
            <h2 className="section-title">{t.team.title}</h2>
            <p className="section-lead">{t.team.lead}</p>
            <ul className="team-grid">
              {t.team.members.map((member, i) => (
                <li key={i} className="team-card">
                  <div className="team-card-avatar">
                    {member.photo ? (
                      <>
                        <img
                          src={member.photo}
                          alt=""
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const fallback = e.target.nextElementSibling;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <span className="team-card-initial" style={{ display: 'none' }}>{member.name.charAt(0)}</span>
                      </>
                    ) : (
                      <span className="team-card-initial">{member.name.charAt(0)}</span>
                    )}
                  </div>
                  <h3 className="team-card-name">{member.name}</h3>
                  <p className="team-card-role">{member.role}</p>
                  <p className="team-card-bio">{member.bio}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="contact" className="section section-contact">
          <div className="container">
            <h2 className="section-title">{t.contact.title}</h2>
            <p className="section-lead">{t.contact.lead}</p>
            <div className="contact-actions">
              <a
                href={enquiryMailto}
                className="btn btn-primary"
              >
                {t.contact.enquiryButton}
              </a>
              <a href="https://basira.me" className="link-muted">{t.contact.domain}</a>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="container">
          <span className="logo-crop logo-crop-footer">
            <img src="/basira-logo.png" alt="" className="footer-logo" />
          </span>
          <p className="footer-copy">
            © 2024 Basira. {t.footer}
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
